/**
 * Utility functions for managing organization data
 */

/**
 * Set the organization ID in localStorage
 * @param {string} organizationId - The organization ID to store
 */
export const setOrganizationId = (organizationId) => {
  if (organizationId) {
    localStorage.setItem('organizationId', organizationId);
  }
};

/**
 * Get the organization ID from localStorage
 * @returns {string|null} - The organization ID or null if not found
 */
export const getOrganizationId = () => {
  return localStorage.getItem('organizationId');
};

/**
 * Remove the organization ID from localStorage
 */
export const removeOrganizationId = () => {
  localStorage.removeItem('organizationId');
};

/**
 * Check if the user has an organization
 * @returns {boolean} - True if the user has an organization, false otherwise
 */
export const hasOrganization = () => {
  return !!getOrganizationId();
};

/**
 * Set the subscription choice in localStorage
 * @param {Object} subscription - The subscription object to store
 */
export const setSubscriptionChoice = (subscription) => {
  if (subscription) {
    localStorage.setItem('subscriptionChoice', JSON.stringify(subscription));
  }
};

/**
 * Get the subscription choice from localStorage
 * @returns {Object|null} - The subscription object or null if not found
 */
export const getSubscriptionChoice = () => {
  const subscription = localStorage.getItem('subscriptionChoice');
  return subscription ? JSON.parse(subscription) : null;
};

/**
 * Remove the subscription choice from localStorage
 */
export const removeSubscriptionChoice = () => {
  localStorage.removeItem('subscriptionChoice');
};

/**
 * Get the user's role from localStorage
 * @returns {string|null} The user's role (Owner, Admin, Staff) or null if not found
 */
export const getUserRole = () => {
  return localStorage.getItem('userRole');
};

/**
 * Set the user's role in localStorage
 * @param {string} role - The user's role to store
 */
export const setUserRole = (role) => {
  if (role) {
    localStorage.setItem('userRole', role);
  }
};

/**
 * Remove the user's role from localStorage
 */
export const removeUserRole = () => {
  localStorage.removeItem('userRole');
};

/**
 * Check if the current user is an Owner
 * @returns {boolean}
 */
export const isOwner = () => {
  return getUserRole() === 'Owner';
};

/**
 * Check if the current user is an Admin or Owner
 * @returns {boolean}
 */
export const isAdminOrOwner = () => {
  const role = getUserRole();
  return role === 'Owner' || role === 'Admin';
};

export default {
  setOrganizationId,
  getOrganizationId,
  removeOrganizationId,
  hasOrganization,
  setSubscriptionChoice,
  getSubscriptionChoice,
  removeSubscriptionChoice,
  getUserRole,
  setUserRole,
  removeUserRole,
  isOwner,
  isAdminOrOwner
};